import React, { useEffect, useState } from "react";
import {
  HeartIcon,
  UserIcon,
  UserGroupIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import {
  UserRole,
  UserRoleLabels,
  getLabelsByLocale,
  Locale,
} from "@packages/global";
import { useAuthStore } from "../stores/authStore";
import { healthApi } from "../services/api";

const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation("app");
  const [healthStatus, setHealthStatus] = useState<any>(null);
  const { user } = useAuthStore();
  const roleLabels = getLabelsByLocale(UserRoleLabels, i18n.language as Locale);

  useEffect(() => {
    checkHealth();
  }, []);

  const checkHealth = async () => {
    try {
      const status = await healthApi.healthCheck();
      setHealthStatus(status);
    } catch (error) {
      console.error("health check failed:", error);
    }
  };

  const stats = [
    {
      title: t("dashboard.systemStatus"),
      value:
        healthStatus?.status === "ok"
          ? t("dashboard.statusOk")
          : t("dashboard.statusError"),
      icon: HeartIcon,
      color: healthStatus?.status === "ok" ? "text-green-400" : "text-red-400",
      bgColor:
        healthStatus?.status === "ok" ? "bg-green-900/20" : "bg-red-900/20",
    },
    {
      title: t("dashboard.currentUser"),
      value: user?.name || "-",
      icon: UserIcon,
      color: "text-blue-400",
      bgColor: "bg-blue-900/20",
    },
    {
      title: t("dashboard.userRole"),
      value: user?.role ? roleLabels[user.role as UserRole] : "-",
      icon: UserGroupIcon,
      color: "text-purple-400",
      bgColor: "bg-purple-900/20",
    },
    {
      title: t("dashboard.serviceVersion"),
      value: healthStatus?.version || "1.0.0",
      icon: Cog6ToothIcon,
      color: "text-indigo-400",
      bgColor: "bg-indigo-900/20",
    },
  ];

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm hover:shadow-lg transition-all duration-300 p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-400 mb-2">
                    {stat.title}
                  </p>
                  <p className={`text-2xl font-semibold ${stat.color}`}>
                    {stat.value}
                  </p>
                </div>
                <div className={`${stat.bgColor} rounded-lg p-3`}>
                  <Icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-4">
          <h2 className="text-xl font-bold text-gray-50 mb-4">
            {t("dashboard.welcome")}
          </h2>
          <p className="text-gray-400 mb-4">{t("dashboard.welcomeDesc")}</p>
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-gray-100 mb-2">
                {t("dashboard.features")}
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-400">
                <li>{t("dashboard.featureList.auth")}</li>
                <li>{t("dashboard.featureList.userMgmt")}</li>
                <li>{t("dashboard.featureList.monitor")}</li>
                <li>{t("dashboard.featureList.responsive")}</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-gray-100 mb-2">
                {t("dashboard.techStack")}
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-400">
                <li>{t("dashboard.techList.frontend")}</li>
                <li>{t("dashboard.techList.backend")}</li>
                <li>{t("dashboard.techList.db")}</li>
                <li>{t("dashboard.techList.state")}</li>
                <li>{t("dashboard.techList.build")}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
